// src/components/clients/NeedsTouchView.tsx
//
// PKT-0913 · the "Needs a touch" view of the Active Clients tab: the same
// client list the Pipeline board renders, ranked by lib/needsTouch.ts.
// READ-ONLY: a row opens the client's detail drawer and nothing else. There is
// no send, no bulk action and no status control on this view.
// PKT-0916 (B): a "Send invoice" section sits at the top — moved in (today or
// earlier), Applied / Payment Received, no sent/overdue/paid invoice. It nudges;
// it does not send.
import React, { useMemo } from "react";
import {
  Box,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  alpha,
} from "@mui/material";
import {
  buildNeedsTouchQueue,
  IMMINENT_MOVE_IN_DAYS,
  IMMINENT_TOUCH_AFTER_DAYS,
  NEEDS_TOUCH_AFTER_DAYS,
  type NeedsTouchRow,
} from "../../lib/needsTouch";
import { awaitingInvoice, type AwaitingInvoiceRow } from "../../lib/moneyTruth";
import { statusConfig } from "./config";
import type { Client } from "./types";

interface NeedsTouchViewProps {
  clients: Client[];
  onViewDetails: (client: Client) => void;
  /** Injectable clock for tests; defaults to now. */
  now?: Date;
  /**
   * PKT-0916 (B) · ids of clients with a sent/overdue/paid invoice.
   * undefined = still loading; null = the invoice read failed. Either way the
   * Send-invoice section lists nobody and SAYS why — an unread invoice table
   * must never look like "everyone is un-invoiced" or "nobody is".
   */
  invoicedClientIds?: Set<string> | null;
}

const formatMovedIn = (row: AwaitingInvoiceRow): string => {
  const label = row.moveIn.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  return row.daysSinceMoveIn === 0 ? `${label} · today` : `${label} · passed ${row.daysSinceMoveIn}d ago`;
};

/** PKT-0916 (B) · moved in, no invoice — the money-first nudge at the top. */
const SendInvoiceSection: React.FC<{
  rows: AwaitingInvoiceRow[];
  state: "loading" | "error" | "ready";
  onViewDetails: (client: Client) => void;
}> = ({ rows, state, onViewDetails }) => (
  <Paper sx={{ mb: 3 }} data-testid="send-invoice-section">
    <Box sx={{ px: 2, py: 1.5, display: "flex", alignItems: "center", gap: 1 }}>
      <Typography variant="subtitle1" fontWeight={600}>
        Send invoice
      </Typography>
      {state === "ready" && (
        <Chip size="small" label={rows.length} color={rows.length > 0 ? "warning" : "default"} />
      )}
    </Box>
    {state === "loading" ? (
      <Typography variant="body2" color="text.secondary" sx={{ px: 2, pb: 2 }}>
        Checking invoices…
      </Typography>
    ) : state === "error" ? (
      <Typography variant="body2" color="error" sx={{ px: 2, pb: 2 }}>
        Could not check invoices, so nobody is listed here. Refresh to try again.
      </Typography>
    ) : rows.length === 0 ? (
      <Typography variant="body2" color="text.secondary" sx={{ px: 2, pb: 2 }}>
        No client is waiting on an invoice.
      </Typography>
    ) : (
      <TableContainer sx={{ overflowX: "auto" }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Client</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Moved in</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => {
              const status = statusConfig[row.client.status];
              return (
                <TableRow
                  key={row.client.id}
                  hover
                  data-testid="send-invoice-row"
                  data-client-id={row.client.id}
                  onClick={() => onViewDetails(row.client)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      onViewDetails(row.client);
                    }
                  }}
                  role="button"
                  tabIndex={0}
                  sx={{ cursor: "pointer" }}
                >
                  <TableCell sx={{ fontWeight: 600, whiteSpace: "nowrap" }}>
                    {row.client.first_name} {row.client.last_name}
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={status?.label ?? row.client.status}
                      sx={{
                        backgroundColor: alpha(status?.color ?? "#64748B", 0.15),
                        color: status?.color ?? "#64748B",
                        fontWeight: 600,
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap", color: "#EF4444", fontWeight: 600 }}>
                    {formatMovedIn(row)}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    )}
  </Paper>
);

export function formatMoveIn(row: NeedsTouchRow): string {
  if (!row.moveIn) return row.moveInText || "—";
  const label = row.moveIn.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  const d = row.daysUntilMoveIn as number;
  if (d === 0) return `${label} · today`;
  if (d > 0) return `${label} · in ${d}d`;
  return `${label} · passed ${-d}d ago`;
}

export function formatTouch(row: NeedsTouchRow): string {
  if (row.daysSinceTouch === null) return "unknown";
  return `${row.daysSinceTouch}d`;
}

const touchColor = (row: NeedsTouchRow): string => {
  if (row.daysSinceTouch === null) return "#EF4444";
  if (row.daysSinceTouch < IMMINENT_TOUCH_AFTER_DAYS) return "#94a3b8";
  if (row.daysSinceTouch < NEEDS_TOUCH_AFTER_DAYS) return "#F59E0B";
  if (row.daysSinceTouch < 14) return "#F59E0B";
  return "#EF4444";
};

const Section: React.FC<{
  title: string;
  testId: string;
  rows: NeedsTouchRow[];
  empty: string;
  onViewDetails: (client: Client) => void;
}> = ({ title, testId, rows, empty, onViewDetails }) => (
  <Paper sx={{ mb: 3 }} data-testid={testId}>
    <Box sx={{ px: 2, py: 1.5, display: "flex", alignItems: "center", gap: 1 }}>
      <Typography variant="subtitle1" fontWeight={600}>
        {title}
      </Typography>
      <Chip size="small" label={rows.length} />
    </Box>
    {rows.length === 0 ? (
      <Typography variant="body2" color="text.secondary" sx={{ px: 2, pb: 2 }}>
        {empty}
      </Typography>
    ) : (
      <TableContainer sx={{ overflowX: "auto" }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Client</TableCell>
              <TableCell>Agent</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Move-in</TableCell>
              <TableCell align="right">Recorded contact age</TableCell>
              <TableCell>Review evidence</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => {
              const status = statusConfig[row.client.status];
              return (
                <TableRow
                  key={row.client.id}
                  hover
                  data-testid="needs-touch-row"
                  data-client-id={row.client.id}
                  onClick={() => onViewDetails(row.client)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      onViewDetails(row.client);
                    }
                  }}
                  role="button"
                  tabIndex={0}
                  sx={{ cursor: "pointer" }}
                >
                  <TableCell sx={{ fontWeight: 600, whiteSpace: "nowrap" }}>
                    {row.client.first_name} {row.client.last_name}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>{row.agentName}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={status?.label ?? row.client.status}
                      sx={{
                        backgroundColor: alpha(status?.color ?? "#64748B", 0.15),
                        color: status?.color ?? "#64748B",
                        fontWeight: 600,
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>{formatMoveIn(row)}</TableCell>
                  <TableCell align="right" sx={{ color: touchColor(row), fontWeight: 600, whiteSpace: "nowrap" }}>
                    {formatTouch(row)}
                  </TableCell>
                  <TableCell>
                    {row.reviewReason}
                    {!row.moveIn && " · Move-in date unknown"}
                    {row.moveInLapsed && " · Move-in passed over 14d ago; verify plans"}
                    {!row.client.assigned_agent_id && " · Owner missing"}
                    {row.client.assigned_agent_id && !row.client.assigned_agent && " · Owner details unavailable"}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    )}
  </Paper>
);

const NeedsTouchView: React.FC<NeedsTouchViewProps> = ({ clients, onViewDetails, now, invoicedClientIds }) => {
  const evaluatedAt = useMemo(() => now ?? new Date(), [clients, now]);
  const queue = useMemo(() => buildNeedsTouchQueue(clients, evaluatedAt), [clients, evaluatedAt]);
  const eligibleRows = [...queue.needsTouch, ...queue.recent];
  const unknownContact = eligibleRows.filter((row) => row.contactEvidence !== "recorded").length;
  const invoiceState: "loading" | "error" | "ready" =
    invoicedClientIds === undefined ? "loading" : invoicedClientIds === null ? "error" : "ready";
  const toInvoice = useMemo(
    () => (invoicedClientIds ? awaitingInvoice(clients, invoicedClientIds, evaluatedAt) : []),
    [clients, invoicedClientIds, evaluatedAt]
  );
  return (
    <Box>
      <Paper sx={{ p: 2, mb: 2 }} data-testid="due-work-coverage">
        <Typography variant="subtitle1">Operator due-work · current filters</Typography>
        <Typography variant="body2">
          {eligibleRows.length} eligible active records · {queue.needsTouch.length} to review · {unknownContact} with unknown contact age
        </Typography>
        <Typography variant="caption" display="block">
          Evaluated at {evaluatedAt.toISOString()}. Scope: loaded clients after owner, status,
          search and move-in filters; no business-wide total. Use the owner filter for Davis’s queue.
          Paid, closed, out-of-contact, test and synthetic records are excluded from contact review.
          Counts describe current records, not leads, tours, signed leases, invoices or collected payments.
        </Typography>
      </Paper>
      <SendInvoiceSection rows={toInvoice} state={invoiceState} onViewDetails={onViewDetails} />
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
        New, Touring and Applied records with contact age of {NEEDS_TOUCH_AFTER_DAYS}+ days (
        {IMMINENT_TOUCH_AFTER_DAYS}+ days when move-in is within {IMMINENT_MOVE_IN_DAYS} days), soonest move-in
        first. Missing, invalid or future contact dates need verification. This uses clients.last_contacted_at,
        not verified first HUMAN contact. Messages SMS may be absent; check the history before acting.
        These are review candidates, not proof of neglect. Dates use the browser’s local calendar;
        contact age uses elapsed 24-hour days.
      </Typography>
      <Section
        title="Needs a touch"
        testId="needs-touch-section"
        rows={queue.needsTouch}
        empty="No review candidates in the loaded, filtered records. Contact coverage may be incomplete."
        onViewDetails={onViewDetails}
      />
      <Section
        title="Below the contact-review threshold"
        testId="needs-touch-recent-section"
        rows={queue.recent}
        empty="Nobody yet."
        onViewDetails={onViewDetails}
      />
    </Box>
  );
};

export default NeedsTouchView;
